import type { OnboardingStep, OnboardingTour } from '../types';
import {
  getOrdersCategoryValues,
  getOrdersFullPictureValues,
  getOrdersKpiValues,
  getOrdersSplitValues,
} from '../ordersValues';
import {
  hasMultipleRestaurants,
  isViewingAllRestaurants,
} from '../tourRestaurant';
import { tableSearchExercise } from '../tableExercises';

const searchExercise = tableSearchExercise('orders', 20, 'orders-products-search');

const skip = 'onboarding.skip';
const next = 'onboarding.next';
const overview = '/orderhistory';
const products = '/orderhistory/products';

export const ordersSteps: OnboardingStep[] = [
  {
    id: 'orders-welcome',
    route: overview,
    layout: 'hero',
    illustration: 'ordersWelcome',
    titleKey: 'onboarding.orders.welcome.title',
    descriptionKey: 'onboarding.orders.welcome.description',
    primaryLabelKey: 'onboarding.orders.welcome.cta',
    secondaryLabelKey: skip,
  },
  {
    id: 'orders-kpis',
    route: overview,
    target: 'page-kpis',
    layout: 'hero',
    illustration: 'ordersKpis',
    titleKey: 'onboarding.orders.kpis.title',
    descriptionKey: 'onboarding.orders.kpis.description',
    values: getOrdersKpiValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'below',
    padding: 8,
    radius: 16,
  },
  {
    id: 'orders-supplier',
    route: overview,
    target: 'orders-spend-supplier',
    layout: 'compact',
    illustration: 'ordersSupplier',
    titleKey: 'onboarding.orders.supplier.title',
    descriptionKey: 'onboarding.orders.supplier.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
  },
  {
    id: 'orders-category',
    route: overview,
    target: 'orders-spend-category',
    layout: 'compact',
    illustration: 'ordersCategory',
    titleKey: 'onboarding.orders.category.title',
    descriptionKey: 'onboarding.orders.category.description',
    values: getOrdersCategoryValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
  },
  {
    id: 'orders-split',
    values: getOrdersSplitValues,
    route: overview,
    target: 'orders-spending-split',
    layout: 'compact',
    illustration: 'ordersSplit',
    titleKey: 'onboarding.orders.split.title',
    descriptionKey: 'onboarding.orders.split.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    padding: 8,
    radius: 12,
  },
  {
    id: 'orders-top',
    route: overview,
    target: 'orders-top-products',
    layout: 'compact',
    illustration: 'ordersTop',
    titleKey: 'onboarding.orders.top.title',
    descriptionKey: 'onboarding.orders.top.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    padding: 8,
    radius: 12,
  },
  {
    id: 'orders-view-products',
    route: overview,
    target: 'orders-view-products',
    layout: 'spotlight',
    illustration: 'ordersSeeAll',
    titleKey: 'onboarding.orders.seeAll.title',
    descriptionKey: 'onboarding.orders.seeAll.description',
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'above',
    allowTargetInteraction: true,
    highlightTarget: true,
    advanceOnTargetClick: true,
    padding: 8,
    radius: 12,
  },
  {
    id: 'orders-full-picture',
    onEnter: () => { searchExercise.reset(); window.dispatchEvent(new Event('onboarding:orders-clear-search')); },
    route: products,
    target: 'orders-products-page',
    layout: 'compact',
    illustration: 'fullPicture',
    titleKey: 'onboarding.orders.fullPicture.title',
    descriptionKey: 'onboarding.orders.fullPicture.description',
    values: getOrdersFullPictureValues,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'right',
    padding: 8,
    radius: 16,
  },
  ...(['name', 'supplier', 'category', 'price', 'quantity', 'spend'] as const).map(column => ({
    id: `orders-column-${column}`, route: products, target: `orders-column-${column}`, layout: 'compact' as const,
    titleKey: `onboarding.orders.columns.${column}.title`, descriptionKey: `onboarding.orders.columns.${column}.description`,
    primaryLabelKey: next, showArrow: true, placement: 'right' as const,
  })),
  {
    id: 'orders-filters', route: products, target: 'orders-filter-header-control', layout: 'compact',
    titleKey: 'onboarding.orders.filters.title', descriptionKey: 'onboarding.orders.filters.description',
    primaryLabelKey: next, showArrow: true, separateHoles: true,
  },
  {
    id: 'orders-search',
    route: products,
    target: 'orders-products-search',
    layout: 'compact',
    illustration: 'jumpToItem',
    titleKey: 'onboarding.orders.search.title',
    descriptionKey: 'onboarding.orders.search.description',
    ...searchExercise,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    allowTargetInteraction: true,
    highlightTarget: true,
    padding: 6,
    radius: 8,
  },
  {
    isAvailable: searchExercise.hasExample,
    onEnter: searchExercise.restoreSearch,
    id: 'orders-search-result', route: products, target: 'orders-products-table', emphasisTarget: 'orders-search-result', layout: 'compact',
    titleKey: 'onboarding.orders.searchResult.title', descriptionKey: 'onboarding.orders.searchResult.description',
    values: searchExercise.values, primaryLabelKey: next,
  },
  {
    id: 'orders-all-restaurants',
    onEnter: () => { window.dispatchEvent(new Event('onboarding:orders-clear-search')); },
    isAvailable: () => hasMultipleRestaurants() && !isViewingAllRestaurants(),
    route: products,
    target: 'restaurant-selector',
    layout: 'compact',
    illustration: 'click',
    titleKey: 'onboarding.orders.allRestaurants.title',
    descriptionKey: 'onboarding.orders.allRestaurants.description',
    advanceWhen: isViewingAllRestaurants,
    primaryLabelKey: next,
    secondaryLabelKey: skip,
    showArrow: true,
    placement: 'left',
    allowTargetInteraction: true,
    highlightTarget: true,
    padding: 6,
    radius: 8,
  },
  {
    id: 'orders-group-overview', route: products, target: 'orders-products-table', layout: 'compact',
    isAvailable: () => hasMultipleRestaurants() && isViewingAllRestaurants(),
    onEnter: () => { window.dispatchEvent(new Event('onboarding:orders-clear-search')); },
    titleKey: 'onboarding.orders.groupOverview.title', descriptionKey: 'onboarding.orders.groupOverview.description',
    primaryLabelKey: next,
  },
  {
    id: 'orders-complete',
    route: products,
    layout: 'complete',
    illustration: 'completeOrders',
    titleKey: 'onboarding.orders.complete.title',
    descriptionKey: 'onboarding.orders.complete.description',
    primaryLabelKey: 'onboarding.viewCostPrice',
    primaryRoute: '/costprice',
    primaryTour: 'costPrice',
    secondaryLabelKey: 'onboarding.explorePage',
    secondaryAction: 'complete',
  },
];

export const ordersTour: OnboardingTour = {
  id: 'orders',
  paths: ['/orderhistory'],
  steps: ordersSteps,
};
